<script>
import axios from '@/utils/axios';
import HrmPayrollCreateModal from '@/components/modal/hrm-payroll-create-modal.vue';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default {
  components: { HrmPayrollCreateModal },
  data() {
    return {
      loading: true,
      runs: [],
      years: [2024, 2025, 2026, 2027],
      statusOptions: [
        { label: 'Draft', value: 'draft' },
        { label: 'Processed', value: 'processed' },
        { label: 'Finalized', value: 'finalized' },
      ],
      filters: { status: '', year: '' },
      tableColumns: [
        { key: 'period', label: 'Period' },
        { key: 'working_days', label: 'Working Days' },
        { key: 'status', label: 'Status' },
        { key: 'processed', label: 'Processed' },
        { key: 'finalized', label: 'Finalized' },
        { key: 'remarks', label: 'Remarks' },
        { key: 'actions', label: 'Actions', width: '120px' },
      ],
      pagination: { current_page: 1, per_page: 24, last_page: 1, total: 0, links: [] },
    };
  },
  mounted() {
    this.fetchRuns();
  },
  methods: {
    openCreate() { this.$refs.createModal.open(); },
    monthName(m) { return MONTHS[m] || m; },
    async fetchRuns(page = 1) {
      this.loading = true;
      try {
        const res = await axios.get('/hrms/payroll', {
          params: {
            page,
            status: this.filters.status || undefined,
            year: this.filters.year || undefined,
          },
        });
        const d = res.data.data;
        this.runs = d.data;
        this.pagination = { current_page: d.current_page, per_page: d.per_page, last_page: d.last_page, total: d.total, links: d.links };
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.loading = false; }
    },
    openRun(row) {
      this.$router.push({ name: 'hrms-payroll-detail', params: { id: row.id } });
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <h4 class="page-title mb-3">Payroll</h4>
        <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center pr-new-btn" @click="openCreate">
          <i class="ti ti-circle-plus me-2"></i>New Payroll Run
        </a>
      </div>

      <!-- List -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Payroll Runs <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>
          <div class="d-flex gap-2 flex-wrap">
            <vue-select class="pr-vsel" v-model="filters.status" :options="statusOptions" label="label" :reduce="s => s.value"
              placeholder="All Status" :clearable="true" @update:modelValue="fetchRuns(1)" style="min-width:150px;" />
            <vue-select class="pr-vsel" v-model="filters.year" :options="years"
              placeholder="All Years" :clearable="true" @update:modelValue="fetchRuns(1)" style="min-width:120px;" />
          </div>
        </div>

        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">
          <app-table
            :columns="tableColumns" :rows="runs" :pagination="pagination" :loading="loading"
            empty-text="No payroll runs yet." empty-icon="ti ti-calculator-off"
            @page-change="fetchRuns"
          >
            <template #period="{ row }">
              <span class="fw-semibold" style="font-size:13px; color:#1e293b;">{{ monthName(row.month) }} {{ row.year }}</span>
            </template>
            <template #working_days="{ row }">
              <span style="font-size:12.5px; color:#475569;">{{ row.working_days }} days</span>
            </template>
            <template #status="{ row }">
              <span class="pr-st" :class="'pr-' + row.status">{{ row.status }}</span>
            </template>
            <template #processed="{ row }">
              <span v-if="row.processed_at" style="font-size:11.5px; color:#64748b;">{{ $formatDate(row.processed_at) }}</span>
              <span v-else style="color:#cbd5e1;">—</span>
            </template>
            <template #finalized="{ row }">
              <span v-if="row.finalized_at" style="font-size:11.5px; color:#64748b;">{{ $formatDate(row.finalized_at) }}</span>
              <span v-else style="color:#cbd5e1;">—</span>
            </template>
            <template #remarks="{ row }">
              <span style="font-size:12px; color:#64748b;">{{ row.remarks || '-' }}</span>
            </template>
            <template #actions="{ row }">
              <button class="btn btn-sm btn-light" style="color:#4f46e5; font-weight:600;" @click="openRun(row)">
                <i class="ti ti-eye me-1"></i>View
              </button>
            </template>
          </app-table>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-payroll-create-modal ref="createModal" @created="fetchRuns(1)" />
</template>

<style scoped>
.pr-st { display: inline-block; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 20px; text-transform: capitalize; }
.pr-draft { background: #f1f5f9; color: #64748b; }
.pr-processed { background: #dbeafe; color: #2563eb; }
.pr-finalized { background: #dcfce7; color: #16a34a; }
.btn-primary { background: linear-gradient(135deg, #4f46e5, #7c3aed); border: none; }

:deep(.pr-vsel .vs__dropdown-toggle) { border:1px solid #e2e8f0; border-radius:10px; min-height:34px; background:#fff; padding:0 4px; }
:deep(.pr-vsel .vs__dropdown-toggle:focus-within) { border-color:#a5b4fc; box-shadow:0 0 0 3px rgba(79,70,229,.1); }
:deep(.pr-vsel .vs__search) { font-size:12.5px; margin:0; padding:2px 4px; }
:deep(.pr-vsel .vs__selected) { font-size:12.5px; color:#1e293b; margin:2px; }
:deep(.pr-vsel .vs__dropdown-option) { font-size:12.5px; }

@media (max-width: 767px) {
  .page-breadcrumb { flex-direction: column; align-items: stretch !important; gap: 10px; }
  .pr-new-btn { width: 100%; justify-content: center; font-size: 13px; padding: 9px 14px; }

  .card-header { flex-direction: column; align-items: stretch !important; }
  .card-header .d-flex.gap-2.flex-wrap { width: 100%; }
  .card-header .pr-vsel { flex: 1 1 100%; min-width: 100% !important; }
}
</style>